import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, BellOff, CheckCheck, ArrowUpRight, X, AlertTriangle, Check, Hand, FileDown, Smartphone } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, useHasPerm } from "@/context/AuthContext";
import { getNotifMeta, toneClasses, humanTime, agruparPorTiempo, routeForNotif } from "@/lib/notifStyles";
import { playNotifSound, isSoundEnabled, setSoundEnabled } from "@/lib/notifSound";
import { isPushSupported, getPushPermission, getCurrentSubscription, enablePush, disablePush } from "@/lib/push";
import DescargarGuiaMenu from "./DescargarGuiaMenu";

/**
 * Campanita de notificaciones — rediseño Feb 2026:
 *  - Iconos y color específicos por tipo (stock, precio, pedido, etc.)
 *  - Notificaciones agrupadas por tiempo (Hoy · Ayer · Esta semana · Antes)
 *  - Notif crítica con banda rose + icono AlertTriangle
 *  - Estado vacío amable con guía
 *  - Copy claro: "Todo al día" vs "3 sin leer"
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [guiaMenu, setGuiaMenu] = useState(null); // {pedidoId, notif}
  const [pushState, setPushState] = useState("checking"); // "unsupported"|"denied"|"off"|"on"|"checking"
  const [pushBusy, setPushBusy] = useState(false);
  const wrap = useRef(null);

  // Detectar estado de push al montar
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isPushSupported()) { if (alive) setPushState("unsupported"); return; }
      const perm = await getPushPermission();
      if (perm === "denied") { if (alive) setPushState("denied"); return; }
      const sub = await getCurrentSubscription();
      if (alive) setPushState(sub ? "on" : "off");
    })();
    return () => { alive = false; };
  }, []);

  const togglePush = async () => {
    if (pushState === "unsupported") {
      toast.error("Este navegador no soporta notificaciones push.");
      return;
    }
    if (pushState === "denied") {
      toast.error("Bloqueaste las notificaciones. Actívalas desde la configuración del navegador.");
      return;
    }
    setPushBusy(true);
    try {
      if (pushState === "on") {
        await disablePush();
        setPushState("off");
        toast("Notificaciones móviles desactivadas");
      } else {
        await enablePush();
        setPushState("on");
        toast.success("¡Listo! Recibirás notificaciones aunque cierres la app 🔔");
      }
    } catch (e) {
      toast.error(e?.message || "No se pudo activar");
    } finally {
      setPushBusy(false);
    }
  };
  const prevUnread = useRef(0);
  const navigate = useNavigate();
  const canTomarP = useHasPerm("despachos.tomar");
  const canGestP = useHasPerm("despachos.gestionar");
  const canTomar = canTomarP || canGestP;

  const load = async () => {
    try {
      const { data } = await api.get("/notificaciones?limit=12");
      const list = data.items || data || [];
      setItems(list);
      const nu = data.no_leidas ?? list.filter((n) => !n.leida).length;
      if (nu > prevUnread.current && prevUnread.current > 0) {
        setShake(true);
        setTimeout(() => setShake(false), 900);
        // Sonido suave para no perderla (respeta la preferencia del usuario)
        const nueva = list.find((n) => !n.leida);
        try {
          playNotifSound(nueva?.critica ? "attention" : "chime");
        } catch {}
        // Toast in-app con la más reciente sin leer (para no perderla)
        if (nueva) {
          toast(nueva.titulo || "Nueva notificación", {
            description: nueva.descripcion || "",
            action: {
              label: "Ver",
              onClick: () => openNotif(nueva),
            },
          });
        }
      }
      prevUnread.current = nu;
      setUnread(nu);
    } catch (e) { console.warn(e); }
  };

  useEffect(() => {
    load();
    // Jun/2026: 30s y solo con la pestaña visible (10s saturaba el servidor con muchos usuarios)
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 30000);
    // Refresco inmediato ante eventos del negocio (login, aprobar/rechazar pago, etc.)
    const onRefresh = () => load();
    window.addEventListener("notif-refresh", onRefresh);
    // Al recuperar el foco (usuario vuelve a la pestaña) también refrescamos.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("notif-refresh", onRefresh);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const marcarLeida = async (id) => {
    try {
      await api.post(`/notificaciones/${id}/leer`);
      setItems((xs) => xs.map((n) => n.id === id ? { ...n, leida: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { console.warn(e); }
  };
  const marcarTodas = async () => {
    setLoading(true);
    try {
      await api.post("/notificaciones/leer-todas");
      setItems((xs) => xs.map((n) => ({ ...n, leida: true })));
      setUnread(0);
    } catch (e) { console.warn(e); } finally { setLoading(false); }
  };

  const openNotif = async (n) => {
    if (!n.leida) marcarLeida(n.id);
    const target = routeForNotif(n);
    if (target) { setOpen(false); navigate(target); }
  };

  const tomarPedido = async (e, n) => {
    e.stopPropagation();
    if (!n.entity_id) return;
    try {
      await api.post(`/pedidos/${n.entity_id}/tomar`);
      toast.success("Pedido tomado 🙋 Lo tienes en tu bandeja");
      if (!n.leida) marcarLeida(n.id);
      setOpen(false);
      navigate(`/pedidos?open=${n.entity_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo tomar");
    }
  };

  const descargarGuia = async (e, n) => {
    e.stopPropagation();
    const pid = n.entity_id;
    if (!pid) return;
    setGuiaMenu({ pedidoId: pid, notif: n });
  };

  const descargarRecibo = async (e, n) => {
    e.stopPropagation();
    const batch = n.entity_id || n?.data?.batch_id;
    if (!batch) return;
    try {
      const { data } = await api.get(`/reportes/liquidacion-comisiones/${batch}/estado-cuenta`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `mi_recibo_${batch}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      if (!n.leida) marcarLeida(n.id);
      toast.success("Recibo descargado ✓");
    } catch (err) {
      toast.error("No se pudo descargar el recibo");
    }
  };

  const groups = agruparPorTiempo(items);

  return (
    <div ref={wrap} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`relative w-10 h-10 rounded-full border border-border/60 bg-card/70 backdrop-blur-sm hover:bg-accent transition-all flex items-center justify-center ${shake ? "animate-[bell-shake_0.9s_ease-in-out]" : ""}`}
        data-testid="notification-bell"
        aria-label="Notificaciones"
        title={unread > 0 ? `${unread} notificación${unread === 1 ? "" : "es"} sin leer` : "Notificaciones"}
      >
        <Bell className="w-[17px] h-[17px] text-foreground/70" strokeWidth={1.8} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center shadow-rose-glow" data-testid="bell-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
        <style>{`@keyframes bell-shake { 0%,100% { transform: rotate(0); } 15% { transform: rotate(-14deg); } 30% { transform: rotate(11deg); } 45% { transform: rotate(-9deg); } 60% { transform: rotate(6deg); } 75% { transform: rotate(-3deg); } }`}</style>
      </button>

      {open && (
        <div className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-12 w-[calc(100vw-1rem)] sm:w-[400px] max-h-[70vh] sm:max-h-[560px] bg-card border border-border rounded-2xl shadow-soft-lift z-50 flex flex-col overflow-hidden" data-testid="bell-dropdown">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between bg-gradient-to-b from-primary/5 to-transparent">
            <div>
              <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" /> Bandeja
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {unread === 0 ? <><Check className="w-3 h-3 inline -mt-0.5 mr-1" />Todo al día</> : `${unread} sin leer · ${items.length} en total`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {pushState !== "unsupported" && (
                <button
                  onClick={togglePush}
                  disabled={pushBusy}
                  className={`p-1.5 rounded-md hover:bg-accent ${pushState === "on" ? "text-emerald-600" : "text-muted-foreground"}`}
                  title={
                    pushState === "on" ? "Notificaciones móviles activas (tócalo para apagar)" :
                    pushState === "denied" ? "Notificaciones bloqueadas por el navegador" :
                    "Activar notificaciones en el celular (aunque la app esté cerrada)"
                  }
                  data-testid="bell-push-toggle"
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => {
                  const next = !soundOn;
                  setSoundOn(next);
                  setSoundEnabled(next);
                  if (next) { try { playNotifSound("success"); } catch {} }
                  toast(next ? "Sonido activado 🔔" : "Sonido silenciado");
                }}
                className="p-1.5 rounded-md hover:bg-accent text-muted-foreground"
                title={soundOn ? "Silenciar sonido de notificaciones" : "Activar sonido de notificaciones"}
                aria-label={soundOn ? "Silenciar" : "Activar sonido"}
                data-testid="bell-sound-toggle"
              >
                {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
              {unread > 0 && (
                <button onClick={marcarTodas} disabled={loading} className="text-[11px] text-primary hover:bg-primary/10 rounded-md px-2 py-1 font-medium inline-flex items-center gap-1" data-testid="bell-mark-all">
                  <CheckCheck className="w-3.5 h-3.5" /> Todas leídas
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground" aria-label="Cerrar">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
                  <Check className="w-7 h-7 text-emerald-600" />
                </div>
                <div className="text-sm font-medium text-foreground">Bandeja vacía</div>
                <div className="text-[11px] text-muted-foreground mt-1 max-w-[240px] mx-auto">
                  Cuando algo cambie en pedidos, stock o precios, verás el aviso aquí.
                </div>
              </div>
            )}
            {groups.map((g) => (
              <div key={g.label}>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-bold">{g.label}</div>
                {g.items.map((n) => (
                  <BellRow
                    key={n.id}
                    n={n}
                    canTomar={canTomar}
                    onOpen={() => openNotif(n)}
                    onMark={(e) => { e.stopPropagation(); marcarLeida(n.id); }}
                    onTomar={(e) => tomarPedido(e, n)}
                    onDescargarRecibo={(e) => descargarRecibo(e, n)}
                    onDescargarGuia={(e) => descargarGuia(e, n)}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Footer */}
          <NavLink to="/notificaciones" onClick={() => setOpen(false)}
            className="px-4 py-3 text-xs font-medium text-primary hover:bg-primary/5 flex items-center justify-center gap-1.5 border-t border-border/60 transition-colors">
            Ver todas las notificaciones <ArrowUpRight className="w-3.5 h-3.5" />
          </NavLink>
        </div>
      )}
      {guiaMenu && (
        <DescargarGuiaMenu
          pedidoId={guiaMenu.pedidoId}
          nombreArchivo={guiaMenu.pedidoId.slice(0, 8)}
          onDone={() => {
            if (guiaMenu.notif && !guiaMenu.notif.leida) marcarLeida(guiaMenu.notif.id);
          }}
          onClose={() => setGuiaMenu(null)}
        />
      )}
    </div>
  );
}

function BellRow({ n, onOpen, onMark, onTomar, canTomar, onDescargarRecibo, onDescargarGuia }) {
  const meta = getNotifMeta(n.tipo);
  const tone = toneClasses(meta.tone, n.critica);
  const Icon = meta.icon;
  const target = routeForNotif(n);
  const clickable = !!target || !n.leida;
  const puedeTomar = canTomar && n.tipo === "pedido.creado" && n.entity_id;
  const puedeDescargarRecibo = n.tipo === "comisiones.liquidadas" && (n.entity_id || n?.data?.batch_id);
  const puedeDescargarGuia = n.tipo === "pedido.despachado" && n.entity_id && n?.data?.guia_disponible;

  return (
    <div
      onClick={clickable ? onOpen : undefined}
      className={`px-4 py-3 border-b border-border/40 last:border-0 transition-colors relative ${
        n.critica ? "bg-rose-500/5 border-l-[3px] border-l-rose-500" :
        !n.leida ? "bg-primary/[0.03]" : ""
      } ${clickable ? "cursor-pointer hover:bg-accent/40" : ""}`}
      data-testid={`bell-item-${n.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icono */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone.iconBg} relative`}>
          <Icon className={`w-4 h-4 ${tone.iconFg}`} />
          {n.critica && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-rose-500 border-2 border-card flex items-center justify-center">
              <AlertTriangle className="w-2 h-2 text-white" />
            </span>
          )}
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${tone.chip}`}>
              {meta.label}
            </span>
            {n.critica && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-rose-500 text-white">Urgente</span>}
            {!n.leida && <span className="ml-auto w-2 h-2 rounded-full bg-primary" />}
          </div>
          <div className={`text-[13px] leading-snug ${!n.leida ? "font-semibold text-foreground" : "text-foreground/80"}`}>
            {n.titulo}
          </div>
          {n.descripcion && (
            <div className="text-[11.5px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug">{n.descripcion}</div>
          )}
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <div className="text-[10px] text-muted-foreground/80 flex items-center gap-2 min-w-0">
              <span>{humanTime(n.at || n.created_at)}</span>
              {n.actor_nombre && <span className="truncate">· {n.actor_nombre}</span>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {puedeTomar && (
                <button
                  onClick={onTomar}
                  className="text-[10px] h-6 px-2 rounded-full font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-0.5 btn-tactile"
                  title="Tomar este pedido"
                  data-testid={`bell-tomar-${n.id}`}
                >
                  <Hand className="w-2.5 h-2.5" /> Tomar
                </button>
              )}
              {puedeDescargarRecibo && (
                <button
                  onClick={onDescargarRecibo}
                  className="text-[10px] h-6 px-2 rounded-full font-bold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-0.5 btn-tactile"
                  title="Descargar mi recibo (estado de cuenta PDF)"
                  data-testid={`bell-recibo-${n.id}`}
                >
                  <FileDown className="w-2.5 h-2.5" /> Recibo
                </button>
              )}
              {puedeDescargarGuia && (
                <button
                  onClick={onDescargarGuia}
                  className="text-[10px] h-6 px-2 rounded-full font-bold bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-0.5 btn-tactile"
                  title="Ver guía de transporte"
                  data-testid={`bell-guia-${n.id}`}
                >
                  <FileDown className="w-2.5 h-2.5" /> Guía
                </button>
              )}
              {target && (
                <span className="text-[10px] text-primary font-semibold flex items-center gap-0.5">
                  {meta.accion || "Abrir"} <ArrowUpRight className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Acción rápida marcar leída */}
        {!n.leida && (
          <button onClick={onMark} className="p-1.5 rounded-md hover:bg-primary/10 text-primary shrink-0 opacity-70 hover:opacity-100" title="Marcar como leída" data-testid={`bell-mark-${n.id}`}>
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
